import React, { Fragment, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Badge } from 'react-bootstrap';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { setAlert } from '../../../actions/alert';
import { buscarComandas } from '../../../actions/comandas';

import { formatNumber } from '../../../utils/generalFunctions';

import StatusComanda from './StatusComanda';
import EliminarComanda from './EliminarComanda';

const Comandas = () => {

    let rutasBread = [
        {
            activo: true,
            nombre: 'Comandas',
            path: '/comandas'
        }
    ];

    const dispatch =  useDispatch();

    const { usuario } = useSelector(state => state.auth);
    const { listado } = useSelector(state => state.comandas);

    const [comandas, setComandas] = useState([]);

    const [comandaStatus, setComandaStatus] = useState(null);
    const [comandaEliminar, setComandaEliminar] = useState(null);

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const columns = [
        {
            name: 'Evento',
            grow: 1,
            cell: row => row.evento?.tipo_evento ? row.evento.tipo_evento.nombre : 'Sin definir',
        },
        {
            name: 'Paciente',
            grow: 1,
            cell: row => row.evento?.paciente ? row.evento.paciente.nombre : 'Sin definir',
        },
        {
            name: 'Fecha de creación',
            grow: 1.5,
            cell: item => item.fecha && item.fecha != '0000-00-00' ? new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' ' + new Date(item.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : 'Sin definir'
        },
        {
            name: 'Fecha de finalización',
            grow: 1.5,
            cell: item => item.fecha_finalizacion && item.fecha_finalizacion != '0000-00-00' ? new Date(item.fecha_finalizacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' ' + new Date(item.fecha_finalizacion).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : 'Sin definir'
        },
        {
            name: 'Venta',
            grow: 1,
            selector: row => row.venta?.folio ? '#' + row.venta.folio : 'Sin venta'
        },
        {
            name: 'Médico',
            cell: row => row.usuario ? row.usuario.nombre + ' ' + row.usuario.apellido : 'Sin definir',
        },
        {
            name: 'Productos',
            grow: 0.5,
            selector: row => formatNumber(row.productos, 0, '', '')
        },
        {
            name: 'Servicios',
            grow: 0.5,
            selector: row => formatNumber(row.servicios, 0, '', '')
        },
        {
            name: 'Status',
            selector: (row, index) => {
                switch(row.status){
                    case 1:
                        return(
                            <h5><button type="button" className="btn btn-link btn-lg p-0" onClick={() => handleStatusComanda(row)}><Badge pill bg="secondary">Activa</Badge></button></h5>
                        )

                    case 2:
                        return(
                            <h5><button type="button" className="btn btn-link btn-lg p-0" onClick={() => handleStatusComanda(row)}><Badge pill bg="success">Finalizada</Badge></button></h5>
                        )
                    default:
                        break;
                }
            }
        },
        {
            name: '',
            grow: 0.5,
            cell: row => {
                return(
                    <div className="text-right w-100">
                        {
                            usuario?.permisos?.comandas?.eliminar && puedeEliminarComanda(row) &&
                                <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEliminarComanda(row)}><i className="fas fa-trash text-danger"></i></button>
                        }
                        {
                            row.status == 1 && usuario?.permisos?.comandas?.editar &&
                                <Link className="btn btn-sm btn-link px-2 float-end" to={`/comandas/editar/${row.id}`}><i className="fas fa-edit text-warning"></i></Link>
                        }
                    </div>
                )
            }
        }
    ];

    /* Elegibilidad de eliminación (reflejo de las reglas que ya valida el backend) */
    const puedeEliminarComanda = (comanda) => {
        if(comanda?.id_venta) return false;
        if(comanda?.status == 1) return true;

        if(comanda?.status == 2){
            if(!comanda?.fecha_finalizacion) return false;

            const horas_transcurridas = (new Date().getTime() - new Date(comanda?.fecha_finalizacion).getTime()) / (1000 * 60 * 60);

            return horas_transcurridas <= 48;
        }

        return false;
    }

    /* Elegibilidad de reversión (reflejo de la misma regla de 48h que ya valida el backend) */
    const puedeRevertirComanda = (comanda) => {
        if(!comanda?.fecha_finalizacion) return false;

        const horas_transcurridas = (new Date().getTime() - new Date(comanda?.fecha_finalizacion).getTime()) / (1000 * 60 * 60);

        return horas_transcurridas <= 48;
    }

    /* Status comanda */
    const handleStatusComanda = (comanda) => {
        if(comanda?.status == 1 && usuario?.permisos?.comandas?.finalizar){
            setComandaStatus(comanda);
        }else if(comanda?.status == 2 && usuario?.permisos?.comandas?.revertir){
            if(puedeRevertirComanda(comanda)){
                setComandaStatus(comanda);
            }else{
                dispatch(setAlert('Ya pasaron las 48 horas desde que se finalizó esta comanda, no será posible revertir su estatus', 'danger'));
            }
        }
    }

    /* Eliminar comanda */
    const handleEliminarComanda = (comanda) => {
        setComandaEliminar(comanda);
    }

    useEffect(() => {
        dispatch(buscarComandas());
    }, []);

    useEffect(() => {
        setComandas(listado);
    }, [listado]);

    useEffect(() => {
        dispatch(editarBreadcrumb(rutasBread));
    }, [rutasBread]);

    return (
        <Fragment>

            <StatusComanda comanda={comandaStatus} reestablecerComanda={setComandaStatus} />
            <EliminarComanda comanda={comandaEliminar} reestablecerComanda={handleEliminarComanda} />

            <div className="row">
                <div className="col-md-12 px-4 pb-4">
                    <h3 className="mb-3 mt-5">Comandas</h3>

                    <div className="d-flex justify-content-end">
                        {
                            usuario?.permisos?.comandas?.agregar && 
                                <Link to="/comandas/nueva">
                                    <button className="btn btn-primary">Agregar comanda</button>
                                </Link>
                        }
                    </div>

                    <div className="mt-2">
                        <DataTable 
                            columns={columns}
                            data={comandas}
                            pagination
                            paginationPerPage={20}
                            paginationRowsPerPageOptions={[5, 10, 20, 50]}
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover={true}
                        />
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default Comandas;